import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import DataGridTable from '../../Components/DataGrid';
import TruncatedTextCell from '../../Components/TruncatedTextCell';
import { StyledSubHeader } from '../../Components/Styled';
import {
    WF_VIEW_TYPE_TABLE,
    WF_VIEW_TYPE_KEY_VALUE,
    WF_VIEW_TYPE_TEXT,
    WORKFLOW_LOG_RESULT_COLUMN_ORDER,
    WORKFLOW_LOG_RESULT_FIELD_LABELS,
    WORKFLOW_LOG_RESULT_HIDDEN_FIELDS,
    formatCamelCaseToTitle,
} from '../../Utility/workflowConfig';
import './workflow.css';

const getFieldLabel = (field) => {
    if (WORKFLOW_LOG_RESULT_FIELD_LABELS[field]) {
        return WORKFLOW_LOG_RESULT_FIELD_LABELS[field];
    }
    return field.charAt(0).toUpperCase() + field.slice(1);
};

const getOrderedResultKeys = (results) => {
    const keys = Array.from(
        new Set(results.flatMap((row) => Object.keys(row || {})))
    ).filter((key) => !WORKFLOW_LOG_RESULT_HIDDEN_FIELDS.includes(key));

    return keys.sort((a, b) => {
        let idxA = WORKFLOW_LOG_RESULT_COLUMN_ORDER.indexOf(a);
        let idxB = WORKFLOW_LOG_RESULT_COLUMN_ORDER.indexOf(b);
        if (idxA === -1) idxA = 999;
        if (idxB === -1) idxB = 999;
        return idxA - idxB;
    });
};

const buildWorkflowLogResultRows = (results) => (
    results.map((row, idx) => ({
        ...row,
        id: row?.id ?? `${row?.entityId || 'row'}-${idx + 1}`,
    }))
);

const buildWorkflowLogResultColumns = (results) => (
    getOrderedResultKeys(results).map((key) => {
        if (key === 'note') {
            return {
                field: key,
                headerName: getFieldLabel(key),
                flex: 2,
                minWidth: 300,
                editable: false,
                renderCell: ({ value }) => (
                    <TruncatedTextCell
                        text={value}
                        dialogTitle="Note Detail"
                        stripHtml={true}
                    />
                )
            };
        }

        if (key === 'subject' || key === 'body' || key === 'errorMessage') {
            return {
                field: key,
                headerName: getFieldLabel(key),
                flex: key === 'body' ? 1.5 : 1,
                minWidth: key === 'body' ? 200 : 150,
                editable: false,
                renderCell: ({ value }) => (
                    <TruncatedTextCell
                        text={value}
                        dialogTitle={getFieldLabel(key)}
                        stripHtml={key === 'body'}
                    />
                )
            };
        }

        if (key === 'status') {
            return {
                field: key,
                headerName: getFieldLabel(key),
                width: 100,
                editable: false,
                valueGetter: ({ value }) => value || '',
            };
        }

        if (key === 'entityId') {
            return {
                field: key,
                headerName: getFieldLabel(key),
                width: 100,
                editable: false,
            };
        }

        if (key === 'taskActivityId' || key === 'followupId') {
            return {
                field: key,
                headerName: getFieldLabel(key),
                width: 125,
                editable: false,
            };
        }

        if (key === 'sender' || key === 'recipient') {
            return {
                field: key,
                headerName: getFieldLabel(key),
                flex: 1,
                minWidth: key === 'recipient' ? 180 : 150,
                editable: false,
            };
        }

        return {
            field: key,
            headerName: getFieldLabel(key),
            flex: 1,
            minWidth: 120,
            editable: false
        };
    })
);

const StepResultRenderer = ({ step }) => {
    const results = Array.isArray(step.outputJson?.results) ? step.outputJson.results : [];
    const hasResults = results.length > 0;

    const skippedResults = Array.isArray(step.outputJson?.skippedResults) ? step.outputJson.skippedResults : [];
    const hasSkippedResults = skippedResults.length > 0;

    const viewType = step.outputJson?.viewType || ((hasResults || hasSkippedResults) ? WF_VIEW_TYPE_TABLE : null);

    const gridData = useMemo(() => buildWorkflowLogResultRows(results), [results]);
    const gridColumns = useMemo(() => buildWorkflowLogResultColumns(results), [results]);

    const skippedGridData = useMemo(() => buildWorkflowLogResultRows(skippedResults), [skippedResults]);
    const skippedGridColumns = useMemo(() => buildWorkflowLogResultColumns(skippedResults), [skippedResults]);

    const summaryMetrics = useMemo(() => {
        if (!step.outputJson) return [];
        const excludedKeys = ['viewType', 'results', 'skippedResults', 'properties', 'text', 'sampleRecords', 'filters'];
        return Object.entries(step.outputJson)
            .filter(([k, v]) => !excludedKeys.includes(k) && typeof v !== 'object')
            .map(([k, v]) => ({ label: getFieldLabel(k), value: v }));
    }, [step.outputJson]);

    if (!viewType) return null;

    switch (viewType) {
        case WF_VIEW_TYPE_TABLE:
            return (
                <Box className="workflow-result-section">
                    {summaryMetrics.length > 0 && (
                        <Box className="workflow-dialog-card-times workflow-result-summary">
                            {summaryMetrics.map(({ label, value }) => (
                                <Typography key={label} variant="body2" className="workflow-field-container">
                                    <strong className="workflow-field-label">{label}:</strong>&nbsp;{String(value)}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    {hasResults && (
                        <Box className={hasSkippedResults ? "workflow-results-list workflow-results-list-spaced" : "workflow-results-list"}>
                            <StyledSubHeader className="mb-10">
                                {hasSkippedResults ? `Prepared Customers (${results.length})` : 'Results'}
                            </StyledSubHeader>
                            <DataGridTable
                                isCheckboxReq={false}
                                isRequiredToolBar={false}
                                height="220px"
                                dataGridData={gridData}
                                columns={gridColumns}
                                marginTop="0px"
                            />
                        </Box>
                    )}

                    {hasSkippedResults && (
                        <Box className="workflow-result-section">
                            <StyledSubHeader className="mb-10">
                                Skipped Customers ({skippedResults.length})
                            </StyledSubHeader>
                            <DataGridTable
                                isCheckboxReq={false}
                                isRequiredToolBar={false}
                                height="200px"
                                dataGridData={skippedGridData}
                                columns={skippedGridColumns}
                                marginTop="0px"
                            />
                        </Box>
                    )}
                </Box>
            );

        case WF_VIEW_TYPE_KEY_VALUE:
            return (
                <Box className="workflow-result-section">
                    <StyledSubHeader className="mb-10">
                        Details
                    </StyledSubHeader>
                    {Object.entries(step.outputJson?.properties || {}).map(([k, v]) => (
                        <Typography key={k} variant="body2" className="workflow-field-container-mt">
                            <span className="workflow-field-label">{formatCamelCaseToTitle(k)}:</span> {String(v)}
                        </Typography>
                    ))}
                </Box>
            );

        case WF_VIEW_TYPE_TEXT:
            return (
                <Box className="workflow-result-section">
                    <StyledSubHeader className="mb-10">
                        Details
                    </StyledSubHeader>
                    <Typography variant="body2" className="workflow-field-container-mt">
                        {step.outputJson?.text || ''}
                    </Typography>
                </Box>
            );

        default:
            return null;
    }
};

export default StepResultRenderer;
